"use client";

import { format } from "date-fns";
import { ChevronDown } from "lucide-react";
import { Fragment, SetStateAction, useCallback, useState } from "react";
import { DateRange } from "react-day-picker";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuRadioItem,
  DropdownMenuRadioGroup,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import * as m from "@/paraglide/messages";

import SearchCoinFilter from "./searchCoins";
import {
  orderTypesEnums,
  ordersSideEnums,
  ordersSourcesEnums,
  ordersStatusEnums,
} from "../types";

export default function Filters() {
  const [selectedCoin, setSelectedCoin] = useState<string>();
  const [side, setSide] = useState<string>();
  const [source, setSource] = useState<string>();
  const [type, setType] = useState<string>();
  const [status, setStatus] = useState<string>();
  const [date, setDate] = useState<DateRange | undefined>();

  const onSelectCoin = useCallback((coin: string) => setSelectedCoin(coin), []);
  const onReset = useCallback(() => {
    const resetEvent = new CustomEvent("reset-orders");
    setSelectedCoin(undefined);
    setSide(undefined);
    setSource(undefined);
    setType(undefined);
    setStatus(undefined);
    setDate(undefined);
    window.dispatchEvent(resetEvent);
  }, []);

  const onSearch = useCallback(() => {
    if (selectedCoin || side || source || type || status || date) {
      const resetEvent = new CustomEvent("search-orders", {
        detail: {
          selectedCoin,
          side,
          source,
          type,
          status,
          date,
        },
      });

      window.dispatchEvent(resetEvent);
    }
  }, [date, selectedCoin, side, source, status, type]);

  const DropdownComponent = useCallback(
    ({
      title,
      setValue,
      keys,
      shouldSwipe,
    }: {
      title: string;
      setValue: React.Dispatch<SetStateAction<string | undefined>>;
      keys: any;
      shouldSwipe?: boolean;
    }) => {
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size="sm"
              variant="outline"
              className="flex items-center justify-between gap-2 px-4 py-2 rounded-sm lg:min-w-[150px] min-w-full"
            >
              {title}
              <ChevronDown className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="lg:w-48 md:w-[calc(100vw-100px)] w-[calc(100vw-70px)] p-2 rounded-sm shadow-lg">
            <DropdownMenuRadioGroup onValueChange={setValue}>
              {Object.keys(keys).map((value, index, arr) => {
                const readableValue = shouldSwipe
                  ? value
                  : keys[value as keyof typeof keys];
                return (
                  <Fragment key={index}>
                    <DropdownMenuRadioItem
                      value={value}
                      className="hover:bg-muted cursor-pointer"
                    >
                      <div className="w-full h-[34px] flex items-center">
                        <span>{readableValue}</span>
                      </div>
                    </DropdownMenuRadioItem>
                    {index === arr.length - 1 ? (
                      <></>
                    ) : (
                      <DropdownMenuSeparator />
                    )}
                  </Fragment>
                );
              })}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
    [],
  );

  return (
    <main className="flex gap-2 pt-4 flex-wrap mb-4">
      <SearchCoinFilter onSelectCoin={onSelectCoin} />
      <DropdownComponent
        keys={orderTypesEnums}
        setValue={setType}
        title={
          orderTypesEnums[type as keyof typeof orderTypesEnums] ?? m.type()
        }
      />
      <DropdownComponent
        keys={ordersSourcesEnums}
        setValue={setSource}
        title={
          ordersSourcesEnums[source as keyof typeof ordersSourcesEnums] ??
          m.source()
        }
      />
      <DropdownComponent
        keys={ordersSideEnums}
        setValue={setSide}
        title={
          ordersSideEnums[side as keyof typeof ordersSideEnums] ?? m.side()
        }
      />
      <DropdownComponent
        keys={ordersStatusEnums}
        setValue={setStatus}
        title={status ?? m.status()}
        shouldSwipe
      />

      <Popover>
        <PopoverTrigger asChild>
          <Button
            size="sm"
            variant="outline"
            className="flex items-center gap-2 px-4 py-2 rounded-sm lg:min-w-[150px] min-w-full justify-between"
          >
            {date?.from ? (
              date.to ? (
                <>
                  {format(date.from, "LLL dd, y")} -{" "}
                  {format(date.to, "LLL dd, y")}
                </>
              ) : (
                format(date.from, "LLL dd, y")
              )
            ) : (
              <span>{m.date()}</span>
            )}
            <ChevronDown className="w-4 h-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-4 rounded-sm shadow-lg lg:w-fit w-full">
          <Calendar
            mode="range"
            selected={date}
            onSelect={(range) => {
              setDate(range);
            }}
            numberOfMonths={2}
          />
        </PopoverContent>
      </Popover>
      <div className="flex gap-2">
        <Button size="sm" className="px-6 py-2 rounded-sm" onClick={onSearch}>
          {m.search()}
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="px-6 py-2 rounded-sm"
          onClick={onReset}
        >
          {m.reset()}
        </Button>
      </div>
    </main>
  );
}
